import { useState, type CSSProperties, type ReactNode } from 'react'
import { Heart } from 'lucide-react'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { addDays, type DayMark, type SpanPart } from '@/lib/eventCalendar'

// The viewer's plan is the day itself, drawn like the RSVP control's own icons:
// going is the check's ringed green disc (a continuous band across a multi-day
// event), interested is the filled heart. A friends' plan sits under the number
// as a cyan dot or line and never fills the cell. A band's rounded ends line up
// with where a single day's disc would sit, so the two read alike.
const SHAPE: Record<SpanPart, string> = {
  single: 'left-1/2 w-8 -translate-x-1/2 rounded-full',
  start: 'left-[calc(50%-1rem)] right-0 rounded-l-full',
  middle: 'inset-x-0',
  end: 'left-0 right-[calc(50%-1rem)] rounded-r-full',
}

const GOING_RING: Record<SpanPart, string> = {
  single: 'border-2',
  start: 'border-2 border-r-0',
  middle: 'border-y-2',
  end: 'border-2 border-l-0',
}

const FRIENDS: Record<SpanPart, string> = {
  single: 'left-1/2 w-1 -translate-x-1/2 rounded-full',
  start: 'left-[calc(50%-0.5rem)] right-0 rounded-l-full',
  middle: 'inset-x-0',
  end: 'left-0 right-[calc(50%-0.5rem)] rounded-r-full',
}

// Past days sit at 35%. A band or line running from yesterday into today
// brightens across the seam instead of stepping: alpha climbs linearly from
// yesterday's center to today's, so each half meets the other at the midpoint.
const DIM = 0.35
const SEAM = (1 + DIM) / 2
const FADE_OUT: CSSProperties = { maskImage: `linear-gradient(to right, rgb(0 0 0 / ${DIM}) 50%, rgb(0 0 0 / ${SEAM}))` }
const FADE_IN: CSSProperties = { maskImage: `linear-gradient(to right, rgb(0 0 0 / ${SEAM}), #000 50%)` }

export function PlansCalendarDay({
  day,
  mark,
  today,
  outside,
  crewHidden,
  settled,
  open,
  plans,
  onToggle,
}: {
  day: string
  mark: DayMark | undefined
  today: string
  /** A day outside the month of the page it's drawn on. */
  outside: boolean
  /** The crew filter is off: the crew mark stays, grayed out. */
  crewHidden?: boolean
  /** The marks have finished loading: a crew mark arriving from here on is new. */
  settled: boolean
  open: boolean
  /** The day's plans, or that it has none, shown in a popover when the day is tapped. */
  plans: (day: string) => ReactNode
  onToggle: (day: string, open: boolean, marked: boolean) => void
}) {
  const mine = mark?.mine
  const friends = mark?.friends
  const past = day < today
  // Yesterday drawn on today's page: on the 1st, yesterday is outside it.
  const yesterday = addDays(day, 1) === today && outside === (day.slice(0, 7) !== today.slice(0, 7))
  const seam = (span: SpanPart | undefined): CSSProperties | undefined => {
    if (yesterday && (span === 'start' || span === 'middle')) return FADE_OUT
    if (day === today && !outside && (span === 'middle' || span === 'end')) return FADE_IN
  }
  const mineSeam = mine?.status === 'going' ? seam(mine.span) : undefined
  const friendsSeam = seam(friends?.span)
  // Yesterday dims each part on its own, so a mark fading across the seam can rise past 35%.
  const dimParts = yesterday && (mineSeam || friendsSeam)
  const dimPart = (faded: boolean) => dimParts && !faded && 'opacity-35'
  // A crew mark that arrives on a settled calendar grows in; one present when
  // the cell mounts, or that lands while the feed is still loading, just appears
  // — growing each cell of a multi-day line on its own reads as dots stretching.
  const [prev, setPrev] = useState({ friends: !!friends, settled })
  const [friendsEntering, setFriendsEntering] = useState(false)
  if (prev.friends !== !!friends || prev.settled !== settled) {
    setPrev({ friends: !!friends, settled })
    if (prev.friends !== !!friends) setFriendsEntering(!!friends && prev.settled)
  }
  const cell = (
    <button
      type="button"
      data-day={day}
      data-mine={mine?.status}
      data-friends={friends ? '' : undefined}
      className={cn(
        'flex w-full cursor-pointer flex-col items-center gap-1 py-1',
        (outside || past) && !dimParts && 'opacity-35',
      )}
    >
      {/* The viewer's own plan anchors the popover to its disc or heart, not the
          whole cell, so the arrow lands on it. */}
      <MaybeAnchor anchored={!!mine}>
        <span className="relative flex h-8 w-full items-center justify-center">
          {mine?.status === 'going' && (
            <span aria-hidden className={cn('absolute inset-0', dimPart(!!mineSeam))} style={mineSeam}>
              <span className={cn('absolute inset-y-0 border-going-foreground bg-going', SHAPE[mine.span], GOING_RING[mine.span])} />
            </span>
          )}
          {mine?.status === 'interested' && (
            <Heart aria-hidden className={cn('absolute left-1/2 top-1/2 size-9 -translate-x-1/2 -translate-y-1/2 fill-interested text-interested', dimPart(false))} />
          )}
          <span
            className={cn(
              'relative text-sm tabular-nums',
              dimPart(false),
              mine?.status === 'going' && 'font-semibold text-going-foreground',
              mine?.status === 'interested' && '-mt-0.5 font-semibold text-interested-foreground',
              day === today && (mine ? 'font-bold underline underline-offset-2' : 'font-bold text-primary'),
            )}
          >
            {Number(day.slice(8))}
          </span>
        </span>
      </MaybeAnchor>
      <span className={cn('relative h-1 w-full', friends && dimPart(!!friendsSeam))} style={friendsSeam}>
        {friends && (
          <span
            aria-hidden
            className={cn(
              'absolute inset-y-0 transition-colors duration-300',
              FRIENDS[friends.span],
              // Grey, like a hidden crew mark, once the viewer said no to all of it.
              crewHidden || friends.declined ? 'bg-muted-foreground/50' : 'bg-friends',
              friendsEntering && (friends.span === 'single' ? 'animate-mark-in' : 'animate-in fade-in duration-300'),
            )}
          />
        )}
      </span>
    </button>
  )
  return (
    <Popover open={open} onOpenChange={next => onToggle(day, next, !!mark)}>
      <PopoverTrigger asChild>{cell}</PopoverTrigger>
      <PopoverContent
        // Above the day so the weeks below stay visible; it flips down when the
        // sticky header leaves no room.
        side="top" sideOffset={3} collisionPadding={{ top: appHeaderHeight() + 12, right: 12, bottom: 12, left: 12 }}
        arrow className="w-72 gap-1 p-1.5" data-testid="plans-day-popover">
        {plans(day)}
      </PopoverContent>
    </Popover>
  )
}

function MaybeAnchor({ anchored, children }: { anchored: boolean; children: ReactNode }) {
  return anchored ? <PopoverAnchor asChild>{children}</PopoverAnchor> : children
}

function appHeaderHeight() {
  return parseFloat(document.documentElement.style.getPropertyValue('--app-header-h')) || 0
}
